import React from 'react';
import { getWeekDatesRange } from '../WeeklyDateNavigation';
import {
  WeekDatesRange,
  WeeklyDatePickerProps,
  DatePickerFirstDayOfWeek,
} from './types';
import { getFirstDayInMonth } from './util/dateUtils';

type WeeklyDatePickerContextValue =
  | {
      currentWeek: WeekDatesRange;
      setCurrentWeek: (week: WeekDatesRange, cb?: Function) => void;
      currentMonth: Date;
      setCurrentMonth: (month: Date) => void;
    }
  | undefined;

const WeeklyDatePickerContext =
  React.createContext<WeeklyDatePickerContextValue>(undefined);

export const useWeeklyDatePickerContext = () => {
  const context = React.useContext(WeeklyDatePickerContext);
  if (!context) {
    throw new Error(
      'useWeeklyDatePicker must be used within a WeeklyDatePickerProvider.'
    );
  }

  return context;
};

export const WeeklyDatePickerProvider: React.FC<{
  selectedDate: Date;
  firstDayOfWeek: DatePickerFirstDayOfWeek;
  onDisplayedWeekChange: WeeklyDatePickerProps['onDisplayedWeekChange'];
  children?: React.ReactNode;
}> = ({ firstDayOfWeek, selectedDate, onDisplayedWeekChange, children }) => {
  const [currentWeek, setWeek] = React.useState<WeekDatesRange>(
    getWeekDatesRange(selectedDate, firstDayOfWeek)
  );
  const [currentMonth, setMonth] = React.useState(
    getFirstDayInMonth(currentWeek.startOfWeek)
  );

  const setCurrentWeek: WeeklyDatePickerContextValue['setCurrentWeek'] = (
    week,
    callBack
  ) => {
    setWeek(week);
    callBack?.();
    if (onDisplayedWeekChange) {
      onDisplayedWeekChange(week);
    }
  };

  const setCurrentMonth: WeeklyDatePickerContextValue['setCurrentMonth'] = (
    month
  ) => {
    setMonth(getFirstDayInMonth(month));
  };

  return (
    <WeeklyDatePickerContext.Provider
      value={{ currentWeek, setCurrentMonth, setCurrentWeek, currentMonth }}
    >
      {children}
    </WeeklyDatePickerContext.Provider>
  );
};
